import type {ReactElement} from 'react';
import {useEffect, useMemo, useRef} from 'react';
import {AiPromptBox} from '@Components/ai-prompt-box/ai-prompt-box';
import {AiPromptBoxSize, PromptBoxState} from '@Components/ai-prompt-box/ai-prompt-box.types';
import {AiPromptBoxUploads} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-uploads/ai-prompt-box-uploads';
import {AiPromptBoxTemplatesButton} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-templates-button/ai-prompt-box-templates-button';
import {AiPromptBoxAspectRatioSelector} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-aspect-ratio-selector/ai-prompt-box-aspect-ratio-selector';
import {isAspectRatioPinned} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-aspect-ratio-selector/ai-prompt-box-aspect-ratio-selector.library';
import {AiPromptBoxBrandKitSelector} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-actions/ai-prompt-box-brand-kit-selector/ai-prompt-box-brand-kit-selector';
import {getDefaultAttachments} from '@Components/ai-prompt-box/libraries/ai-prompt-box-attachment.library';
import type {FooterLeftAction, FooterLeftActionProps} from '@Components/ai-prompt-box/components/ai-prompt-box-footer-left-actions/ai-prompt-box-footer-left-actions.types';
import {useInitAiPromptBox} from '@Components/ai-prompt-box/hooks/useInitAiPromptBox';
import {AiChatSource} from '@Components/ai-chat-window/ai-chat-window.types';
import {AiChatDragDropOverlay} from '@/ai-chat/components/ai-chat-drag-drop-overlay';
import {checkDailyLimit, gateAiChatGeneration, getConsolidatedAttachmentsForAiChat} from '@Libraries/ai-chat.library';
import {buildRequestPreferences, createUserRequestParts} from '@Components/ai-chat-window/lib/ai-chat-messages.library';
import {useAiChat} from '@/ai-chat/providers/ai-chat.context';
import {AI_CHAT_ASSETS_PROMPT_BOX_ID} from './ai-chat-assets-prompt-box.types';
import styles from './ai-chat-assets-prompt-box.module.scss';

export function AiChatAssetsPromptBox(): ReactElement {
  const {state, actions: chatActions} = useAiChat();
  const isSmall = state.isSmall;

  const getInitialPromptBoxSize = (): AiPromptBoxSize => {
    if (isSmall) {
      return AiPromptBoxSize.SMALL_EXTENDED;
    }

    return AiPromptBoxSize.SINGLE_LINE;
  };

  const initialConfig = {
    prompt: '',
    size: getInitialPromptBoxSize(),
    placeholder: window.i18next.t('pmwjs_describe_the_design_you_want_to_create_elipses'),
    promptBoxState: PromptBoxState.IDLE,
    attachments: getDefaultAttachments(),
    source: AiChatSource.AI_CHAT_PAGE,
    showInitialAnimation: true,
    transcription: {showMic: true},
  };

  const {aiPromptBox} = useInitAiPromptBox(AI_CHAT_ASSETS_PROMPT_BOX_ID, initialConfig);
  const {state: aiPromptBoxState, actions} = aiPromptBox;
  const promptBoxContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    void checkDailyLimit(actions.update);
  }, []);

  const isActive = aiPromptBoxState.promptBoxState !== PromptBoxState.IDLE;
  const isUsingMic = Boolean(aiPromptBoxState.isRecordingAudio) || Boolean(aiPromptBoxState.isProcessingAudio);
  const hasContent = aiPromptBoxState.prompt !== '' || (aiPromptBoxState.attachments?.items.length ?? 0) > 0;
  const hasPinnedAspectRatio = isAspectRatioPinned(aiPromptBoxState.pinnedAspectRatio);
  const hasPinnedBrandKit = Boolean(aiPromptBoxState.pinnedBrandKitId);
  const desiredSize =
    isSmall || isActive || isUsingMic || hasContent || hasPinnedAspectRatio || hasPinnedBrandKit ? AiPromptBoxSize.SMALL_EXTENDED : AiPromptBoxSize.SINGLE_LINE;

  useEffect(() => {
    actions.update({size: desiredSize});
  }, [desiredSize]);

  const generate = async (): Promise<void> => {
    if (!(await gateAiChatGeneration(aiPromptBox, state.credits, []))) {
      return;
    }

    const parts = createUserRequestParts(aiPromptBoxState.prompt, getConsolidatedAttachmentsForAiChat(aiPromptBoxState, []));
    chatActions.startNewChatWithParts(parts, buildRequestPreferences(aiPromptBoxState.pinnedAspectRatio ?? null, aiPromptBoxState.pinnedBrandKitId ?? null));
    actions.update({prompt: '', attachments: getDefaultAttachments(), pinnedAspectRatio: null, pinnedBrandKitId: null, promptBoxState: PromptBoxState.IDLE});
  };

  const onFocus = (): void => {
    void checkDailyLimit(actions.update);
  };

  const promptBoxOptions = useMemo<FooterLeftAction[]>(() => {
    return [
      (actionProps: FooterLeftActionProps): ReactElement | null => <AiPromptBoxUploads {...actionProps} hideSelectorBorder />,
      AiPromptBoxTemplatesButton,
      AiPromptBoxBrandKitSelector,
      AiPromptBoxAspectRatioSelector,
    ];
  }, []);

  return (
    <div ref={promptBoxContainerRef} className={styles.container}>
      <AiPromptBox
        containerClasses="_full-width"
        id={AI_CHAT_ASSETS_PROMPT_BOX_ID}
        onGenerate={generate}
        initialConfig={initialConfig}
        onRegenerate={generate}
        options={promptBoxOptions}
        onFocus={onFocus}
      />
      <AiChatDragDropOverlay promptBoxId={AI_CHAT_ASSETS_PROMPT_BOX_ID} targetElement={promptBoxContainerRef} />
    </div>
  );
}
